import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '../../store';
import { api, DropFolderFile } from '../../utils/api';
import { plLabel, LevelLabel } from '../Jobs';
import DropFolderPicker from '../Jobs/DropFolderPicker';

// Global watcher for deferred job-status-change confirmations. A status change queues a
// confirmation server-side; 10 minutes later this popup asks the operator who made the change to
// re-verify the status / finish date before the email is actually sent. Confirm → send; Re-check
// → open the job editor (and re-arm the 10-min timer); Cancel → re-arm the timer. It reappears
// every 10 minutes until confirmed. Mounted once, app-wide.

const POLL_MS = 60_000;
const fmtDate = (ymd: string) => { const p = String(ymd || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : (ymd || '—'); };

function recipientPreview(row: any): { to: string; cc: string } {
  // Mirror the server (2026-07-17 rule): To = the customer ONLY — individual staff are NOT in To
  // because the office mailbox in Cc reaches everyone internally. Popup cases (the 4 confirm
  // statuses / a finish-date change) always include the customer; with no valid customer email the
  // office bucket becomes the To.
  const isLegal = /Legal/i.test(row.company_name || '');
  const office = isLegal ? 'office@slf-legal.co' : 'office@slf-accounting.co';
  // Mirrors the server fix of 2026-08-31: the field routinely holds SEVERAL addresses
  // ("a@x.com, b@y.com, c@z.fr") — split and validate each, or the whole string fails on the
  // comma and the preview claims the client will not be mailed when they will.
  const clients = Array.from(new Set(String(row.client_email || '').split(/[,;]+/)
    .map((e: string) => e.trim().toLowerCase())
    .filter((e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e) && e !== office)));
  const to = clients;
  const cc = Array.from(new Set([office, 'office@samuilawfirm.com'])).filter(e => !to.includes(e) && !(to.length === 0 && e === office));
  return { to: to.length ? to.join(', ') : office, cc: cc.join(', ') };
}


// ── 完成 Job 的必收文档 (Calem 2026-08-25 → owner 2026-09-10) ────────────────────────────────────
// Marking a job Complete tells the client it is done, so the proof is collected HERE, before the
// green button exists. Each row states the name the file will be filed under (composed from the
// four-part protocol, editable before saving — owner decision 1) and the folder in Our Documents
// it will land in, so the operator sees both without leaving the popup.
function CompletionDocs({ jobId, onState, onPlan }: { jobId: string; onState: (s: any) => void; onPlan: (p: any) => void }) {
  const { t } = useTranslation();
  const [state, setState] = useState<any>(null);
  const [busyKey, setBusyKey] = useState('');
  const [err, setErr] = useState('');
  const [draft, setDraft] = useState<Record<string, { name: string; date: string; company?: string }>>({});
  // The row whose file is being picked from the Drop Folder — the only place these documents come
  // from (Calem, report 296f15b9: "Only from the Drop Folder").
  const [picking, setPicking] = useState<any>(null);
  // Re-entry Stamp only: the picked file is held here, classified, until the operator confirms or
  // corrects Single/Multi — never filed straight off the pick like every other row (bug
  // 4275c61e: the old flow saved the ambiguous "Single or Multi" label with no check at all).
  const [reentryDraft, setReentryDraft] = useState<{ node: DropFolderFile; kind: '' | 'single' | 'multi'; detected: '' | 'single' | 'multi'; source: string } | null>(null);

  // The reminders these documents earn. Same plan the Document Storage wizard shows, and shown for
  // the same reason (owner 2026-08-25, restated 2026-09-10): nothing is written to the calendar
  // blindly — the operator sees every event first and can retime or drop any of it.
  const [plan, setPlan] = useState<any[]>([]);
  const load = useCallback(async () => {
    try { const s: any = await api.jobCompletionDocs(jobId); setState(s); onState(s); } catch { /* leave as is */ }
  }, [jobId, onState]);
  useEffect(() => { void load(); }, [load]);

  const uploaded = (state?.rows || []).filter((r: any) => r.uploaded);
  // Only expiry-dated documents belong on the calendar. An issued-dated one (Company Documents, a
  // licence receipt) carries the day it was MADE — turning that into an "EXPIRY —" event would put
  // a false deadline in office@'s calendar (Calem batch 2026-09-10 added the first issued docs).
  const expiring = uploaded.filter((r: any) => r.dateKind === 'expiry');
  useEffect(() => {
    if (!expiring.length) { setPlan([]); onPlan(null); return; }
    const items = expiring.map((r: any) => ({ docType: r.docName, expiryIso: r.uploaded.dateIso }));
    api.docStorageExpiryPlan(items).then((d: any) => {
      const rows = (d.plans || []).map((p: any, i: number) => ({
        docType: items[i].docType, expiryIso: items[i].expiryIso, includeExpiry: true,
        reminders: (p.reminders || []).map((x: any) => ({ ...x, on: true })),
      }));
      setPlan(rows);
    }).catch(() => setPlan([]));
  }, [JSON.stringify(expiring.map((r: any) => [r.docName, r.uploaded.dateIso]))]); // eslint-disable-line

  useEffect(() => {
    onPlan(plan.length ? { items: plan.map(p => ({
      docType: p.docType, expiryIso: p.expiryIso, includeExpiry: p.includeExpiry,
      reminders: p.reminders.filter((r: any) => r.on).map((r: any) => ({ days: r.days, note: r.note })),
    })) } : null);
  }, [JSON.stringify(plan)]); // eslint-disable-line

  if (!state || !state.gated) return null;

  const nameFor = (r: any) => draft[r.key]?.name ?? r.suggestedName;
  const dateFor = (r: any) => draft[r.key]?.date ?? '';
  // A Company TIN's company part (Calem, report a97c67be): prefilled from the client, typed for a person.
  const companyFor = (r: any) => String(draft[r.key]?.company ?? r.company ?? '');
  const companyMissing = (r: any) => !!r.needsCompany && !companyFor(r).trim();
  // Calem 2026-09-10: the name people read is day-first (21-10-2026); ISO stays in the database.
  // ISO in filenames (Calem 2026-09-16, reversing 2026-09-10) — mirrors the server's nameDate.
  const nameDate = (iso: string) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : '');
  // The string the server will write, to the letter — shown under the row so the operator checks
  // it BEFORE the file lands (his bug: the entered date never reached the saved name).
  const finalNameFor = (r: any) => {
    let base = String(nameFor(r) || r.suggestedName || '').trim();
    const d = nameDate(dateFor(r));
    const legacy = d ? d.split('-').reverse().join('-') : '';
    if (legacy && base.includes(legacy)) base = base.split(legacy).join(d);   // typed day-first → ISO
    // The Company box's text goes after the person and before the date, unless the name has it there.
    const co = r.needsCompany ? companyFor(r).trim() : '';
    const tail = d && base.endsWith(` ${d}`) ? ` ${d}` : '';
    const head = base.slice(0, base.length - tail.length);
    if (co && !head.endsWith(` ${co}`)) base = `${head} ${co}${tail}`;
    return d && !base.includes(d) ? `${base} ${d}` : base;
  };
  const setD = (k: string, patch: any) => setDraft(d => ({ ...d, [k]: { name: d[k]?.name ?? '', date: d[k]?.date ?? '', company: d[k]?.company, ...patch } }));

  const pick = async (r: any, node: DropFolderFile) => {
    const date = dateFor(r);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { setErr(t('jc_doc_need_date', { kind: r.dateKind })); return; }
    if (companyMissing(r)) { setErr(t('jc_doc_need_company')); return; }
    setBusyKey(r.key); setErr('');
    try {
      const out: any = await api.jobCompletionDocUpload(jobId, node.id, {
        reqKey: r.key, docName: finalNameFor(r), dateIso: date, folderId: r.folderId,
        company: r.needsCompany ? companyFor(r).trim() : undefined,
      });
      setState(out.state); onState(out.state);
    } catch (e: any) { setErr(e?.message || 'Upload failed'); }
    finally { setBusyKey(''); }
  };
  const drop = async (r: any) => {
    setBusyKey(r.key);
    try { const out: any = await api.jobCompletionDocRemove(jobId, r.uploaded.id); setState(out.state); onState(out.state); }
    catch (e: any) { setErr(e?.message || 'Failed'); }
    finally { setBusyKey(''); }
  };

  // Single vs Multi: swap the qualifier onto whatever name is currently drafted, so an operator's
  // own edits to the person/company parts survive a Single↔Multi toggle.
  const REENTRY_NAME_RX = /^Re-entry Stamp(?: (?:Single or Multi|Single|Multi))?/;
  const withReentryKind = (name: string, kind: 'single' | 'multi') =>
    String(name || '').replace(REENTRY_NAME_RX, `Re-entry Stamp ${kind === 'single' ? 'Single' : 'Multi'}`);
  const pickReentry = async (r: any, node: DropFolderFile) => {
    const date = dateFor(r);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) { setErr(t('jc_doc_need_date', { kind: r.dateKind })); return; }
    setBusyKey(r.key); setErr('');
    try {
      const out: any = await api.jobReentryClassify(jobId, node.id, date);
      const kind = (out.kind === 'single' || out.kind === 'multi') ? out.kind : '';
      setReentryDraft({ node, kind, detected: kind, source: out.source || 'none' });
      setD(r.key, { name: out.suggestedName || nameFor(r) });
    } catch (e: any) { setErr(e?.message || 'Analysis failed'); }
    finally { setBusyKey(''); }
  };
  const chooseReentryKind = (r: any, kind: 'single' | 'multi') => {
    setReentryDraft(d => (d ? { ...d, kind } : d));
    setD(r.key, { name: withReentryKind(nameFor(r), kind) });
  };
  const confirmReentry = async (r: any) => {
    if (!reentryDraft || !reentryDraft.kind) return;
    await pick(r, reentryDraft.node);
    setReentryDraft(null);
  };

  const inp: React.CSSProperties = { padding: '5px 8px', fontSize: 11.5, borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' };
  return (
    <div style={{ margin: '10px 22px', padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-app)' }}>
      <div style={{ fontSize: 12, fontWeight: 800, marginBottom: 2 }}>{t('jc_doc_title')}</div>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 9 }}>{t('jc_doc_hint')}</div>
      {state.rows.map((r: any) => (
        <div key={r.key} style={{ borderTop: '1px solid var(--border)', padding: '9px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 700 }}>
            <i className={r.uploaded ? 'ti ti-circle-check-filled' : r.required ? 'ti ti-alert-circle' : 'ti ti-circle'}
              style={{ fontSize: 14, color: r.uploaded ? '#15803D' : r.required ? '#B45309' : 'var(--text-tertiary)' }} aria-hidden />
            {r.docName}
            <span style={{ fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 999, background: r.required ? 'rgba(180,83,9,0.12)' : 'var(--c-gray-100)', color: r.required ? '#B45309' : 'var(--text-tertiary)' }}>
              {r.required ? t('jc_doc_required') : t('jc_doc_optional')}
            </span>
          </div>
          {r.uploaded ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, fontSize: 11.5 }}>
              <span style={{ color: '#15803D', fontWeight: 700 }}>{r.uploaded.docName}</span>
              <span style={{ color: 'var(--text-tertiary)' }}>→ {r.folderPath || t('jc_doc_no_folder')}</span>
              <button onClick={() => drop(r)} disabled={busyKey === r.key}
                style={{ border: 'none', background: 'transparent', color: '#E24B4A', cursor: 'pointer', fontWeight: 800 }}>×</button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5, flexWrap: 'wrap' }}>
              <input value={nameFor(r)} onChange={e => setD(r.key, { name: e.target.value })}
                title={t('jc_doc_name_tip')} style={{ ...inp, flex: '2 1 260px' }} />
              <input type="date" value={dateFor(r)} onChange={e => setD(r.key, { date: e.target.value })}
                title={t('jc_doc_date_tip', { kind: r.dateKind })} style={{ ...inp, width: 140 }} />
              {/* A Company TIN names a company (Calem, report a97c67be) — the client's, or typed here for
                  a person. data-company-box: the one box here that must be typed in, so its keys do not
                  hide the popup (the `typing` check below). */}
              {r.needsCompany && (
                <label style={{ flex: '1 1 200px', display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                  {t('jc_doc_company')}
                  <input data-company-box value={companyFor(r)} onChange={e => setD(r.key, { company: e.target.value })}
                    title={t('jc_doc_company_tip')} style={{ ...inp, flex: 1, minWidth: 0 }} />
                </label>
              )}
              <div style={{ flexBasis: '100%', fontSize: 11, marginTop: 2 }}>
                <span style={{ color: 'var(--text-tertiary)' }}>{t('jc_doc_stored_as')} </span>
                <b style={{ color: dateFor(r) && !companyMissing(r) ? '#15803D' : '#B45309' }}>{finalNameFor(r)}{companyMissing(r) ? ` — ${t('jc_doc_company_pending')}` : ''}{dateFor(r) ? '' : ` — ${t('jc_doc_date_pending', { kind: r.dateKind })}`}</b>
              </div>
              {r.key === 'reentry' && reentryDraft ? (
                <div style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 3, padding: '7px 9px', borderRadius: 8, background: 'var(--c-gray-50)', border: '1px solid var(--border)' }}>
                  <span style={{ flexBasis: '100%', fontSize: 11.5, fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <i className="ti ti-folder-open" style={{ marginRight: 5, color: 'var(--accent)' }} aria-hidden />{t('jdp_picked', { name: reentryDraft.node.name })}
                  </span>
                  <span style={{ fontSize: 11.5, color: 'var(--text-secondary)' }}>
                    {!reentryDraft.detected
                      ? t('jc_reentry_unclear')
                      : reentryDraft.kind === reentryDraft.detected
                        ? t(reentryDraft.source === 'filename' ? 'jc_reentry_detected_name' : 'jc_reentry_detected_stamp', { value: t(reentryDraft.kind === 'single' ? 'jc_reentry_single' : 'jc_reentry_multi') })
                        : t('jc_reentry_corrected', { value: t(reentryDraft.kind === 'single' ? 'jc_reentry_single' : 'jc_reentry_multi') })}
                  </span>
                  <div style={{ display: 'flex', gap: 5 }}>
                    {(['single', 'multi'] as const).map(k => (
                      <button key={k} type="button" onClick={() => chooseReentryKind(r, k)} disabled={busyKey === r.key}
                        style={{ padding: '4px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                          border: reentryDraft.kind === k ? '1px solid var(--accent)' : '1px solid var(--border-strong)',
                          background: reentryDraft.kind === k ? 'var(--accent)' : 'transparent',
                          color: reentryDraft.kind === k ? '#fff' : 'var(--text-primary)' }}>
                        {t(k === 'single' ? 'jc_reentry_single' : 'jc_reentry_multi')}
                      </button>
                    ))}
                  </div>
                  <button onClick={() => confirmReentry(r)} disabled={!reentryDraft.kind || busyKey === r.key}
                    style={{ padding: '5px 12px', borderRadius: 7, border: 'none', background: 'var(--accent)', color: '#fff', fontSize: 11.5, fontWeight: 700, cursor: reentryDraft.kind ? 'pointer' : 'not-allowed', opacity: reentryDraft.kind ? 1 : 0.6 }}>
                    {busyKey === r.key ? '…' : t('jc_reentry_confirm')}
                  </button>
                  <button onClick={() => setReentryDraft(null)} disabled={busyKey === r.key}
                    style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
                    {t('jc_reentry_cancel')}
                  </button>
                </div>
              ) : (
                <>
                  {/* The date first, as Closed Jobs' manual review asks it — it is part of the name, and
                      so is a Company TIN's company. */}
                  <button onClick={() => { if (!/^\d{4}-\d{2}-\d{2}$/.test(dateFor(r))) { setErr(t('jc_doc_need_date', { kind: r.dateKind })); return; } if (companyMissing(r)) { setErr(t('jc_doc_need_company')); return; } setErr(''); setPicking(r); }}
                    disabled={busyKey === r.key}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 12px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
                    <i className="ti ti-folder-open" style={{ fontSize: 13 }} aria-hidden />{busyKey === r.key ? '…' : t('jdp_choose')}
                  </button>
                  <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>→ {r.folderPath || t('jc_doc_no_folder')}</span>
                </>
              )}
            </div>
          )}
        </div>
      ))}
      {err && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 8 }}>{err}</div>}
      {!!plan.length && (
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 6, paddingTop: 9 }}>
          <div style={{ fontSize: 11.5, fontWeight: 800, marginBottom: 5 }}>
            <i className="ti ti-calendar-plus" style={{ marginRight: 5 }} />{t('jc_cal_title')}
          </div>
          {plan.map((p, i) => (
            <div key={p.docType} style={{ marginBottom: 6 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 700, cursor: 'pointer' }}>
                <input type="checkbox" checked={p.includeExpiry}
                  onChange={e => setPlan(ps => ps.map((x, j) => (j === i ? { ...x, includeExpiry: e.target.checked } : x)))} />
                {t('jc_cal_expiry', { type: p.docType, date: String(p.expiryIso).split('-').reverse().join('/') })}
              </label>
              {p.reminders.map((r: any, k: number) => (
                <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, marginLeft: 18, marginTop: 3, cursor: 'pointer', color: 'var(--text-secondary)' }}>
                  {/* A STANDARD lead (std, from the server's schedule) is not the operator's to
                      adjust — or to untick (owner 2026-09-16). Hand-added ones stay editable. */}
                  <input type="checkbox" checked={r.on} disabled={!!r.std}
                    onChange={e => setPlan(ps => ps.map((x, j) => (j === i ? { ...x, reminders: x.reminders.map((y: any, m: number) => (m === k ? { ...y, on: e.target.checked } : y)) } : x)))} />
                  {r.std ? (
                    <b title="Standard lead time — fixed for this document type" style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                      {r.days}<i className="ti ti-lock" style={{ fontSize: 10, opacity: .55 }} aria-hidden />
                    </b>
                  ) : (
                  <input type="number" min={1} max={600} value={r.days}
                    onChange={e => setPlan(ps => ps.map((x, j) => (j === i ? { ...x, reminders: x.reminders.map((y: any, m: number) => (m === k ? { ...y, days: Math.max(1, Number(e.target.value) || 1) } : y)) } : x)))}
                    style={{ width: 62, padding: '2px 5px', fontSize: 11, borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' }} />
                  )}
                  {t('jc_cal_days_before')}{r.note ? ` · ${r.note}` : ''}
                  {r.custom && (
                    <span onClick={() => setPlan(ps => ps.map((x, j) => (j === i ? { ...x, reminders: x.reminders.filter((_: any, m: number) => m !== k) } : x)))}
                      style={{ cursor: 'pointer', color: '#E24B4A', fontWeight: 800, marginLeft: 2 }}>×</span>
                  )}
                </label>
              ))}
              {!p.reminders.length && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginLeft: 18 }}>{t('jc_cal_none')}</div>}
              {/* A type with no standard schedule (a Re-entry Stamp, whose validity follows whatever
                  visa it sits in) still deserves a reminder — the operator sets it here rather than
                  being sent to the calendar afterwards (owner 2026-09-10). No lead time is invented:
                  the row starts blank-ish and the person who knows the document fills it in. */}
              <button type="button" onClick={() => setPlan(ps => ps.map((x, j) => (j === i
                ? { ...x, reminders: [...x.reminders, { days: 42, note: '', on: true, custom: true }] } : x)))}
                style={{ marginLeft: 18, marginTop: 4, padding: '2px 8px', fontSize: 10.5, fontWeight: 700, borderRadius: 6, border: '1px dashed var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                + {t('jc_cal_add')}
              </button>
            </div>
          ))}
        </div>
      )}
      {!!state.missing.length && (
        <div style={{ fontSize: 11.5, color: '#B45309', marginTop: 8, fontWeight: 700 }}>
          <i className="ti ti-lock" style={{ marginRight: 5 }} />{t('jc_doc_blocked', { list: state.missing.join(', ') })}
        </div>
      )}
      {picking && (
        <DropFolderPicker docName={picking.docName} onClose={() => setPicking(null)}
          onPick={f => { const r = picking; setPicking(null); if (r.key === 'reentry') void pickReentry(r, f); else void pick(r, f); }} />
      )}
    </div>
  );
}

export default function JobStatusConfirm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const loc = useLocation();
  const me = useStore(s => s.me);
  // The Complete gate: while a required proof is missing the green button is not rendered at all
  // (owner 2026-09-10 — hard, no override). Jobs whose type is not in the catalogue report
  // gated:false and behave exactly as before.
  const [docState, setDocState] = useState<any>(null);
  const [calPlan, setCalPlan] = useState<any>(null);
  const showToast = useStore(s => s.showToast);
  const setBlockingError = useStore(s => s.setBlockingError);
  // It used to surface ONLY on a Jobs page, so an operator who set a status and then went back to
  // their mailbox never saw the popup — the client was never told the job was complete and the
  // confirmation sat there until it expired after 7 days. Owner 2026-08-24: pop it wherever the
  // operator is working. The public/no-auth pages are still excluded: a client on /w/<token> or a
  // signer on /contract/<token> must never be shown internal job confirmations.
  const publicPage = /^\/(login|w|dd|contract|portal)(\/|$)/.test(loc.pathname);
  // Don't steal the keyboard from someone mid-sentence — an email composer losing focus (or eating
  // an Enter) is exactly how a reminder turns into a complaint. It re-checks every poll and on blur,
  // so this only postpones the popup by seconds, never drops it.
  // ⚠️ This must key on ACTUAL KEYSTROKES, not on focus. Checking `document.activeElement` was an
  // input suppressed the popup permanently — plenty of pages autofocus a search box, so the
  // condition was true forever and nobody ever saw a confirmation (regression 2026-08-24, caught
  // within the hour). A pause of TYPING_IDLE_MS with no key pressed is enough.
  const TYPING_IDLE_MS = 4000;
  const [typing, setTyping] = useState(false);
  const lastKey = useRef(0);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      // Searching the Drop Folder picker (CompletionDocs) is work inside this popup — hiding it would
      // close the picker under the operator's fingers. So is a Company box (report a97c67be): it can
      // only be filled by typing, and hiding the popup drops what was typed.
      if (el?.closest?.('[data-drop-picker], [data-company-box]')) return;
      const tag = (el?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || el?.isContentEditable) { lastKey.current = Date.now(); setTyping(true); }
    };
    document.addEventListener('keydown', onKey, true);
    // Falls back to false on its own — never a state something can get stuck in.
    const id = setInterval(() => setTyping(Date.now() - lastKey.current < TYPING_IDLE_MS), 1000);
    return () => { document.removeEventListener('keydown', onKey, true); clearInterval(id); };
  }, []);
  const [queue, setQueue] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const meRef = useRef(me);
  meRef.current = me;

  const poll = useCallback(async () => {
    if (!meRef.current || !localStorage.getItem('mf_token')) return;
    try {
      const { rows } = await api.jobConfirmMine();
      setQueue(rows || []);
    } catch { /* transient — try again next tick */ }
  }, []);

  useEffect(() => {
    poll();
    const timer = setInterval(poll, POLL_MS);
    return () => clearInterval(timer);
  }, [poll]);

  // Drop everything the moment the user logs out — never linger over the login screen with the
  // previous user's job/client data.
  useEffect(() => { if (!me) setQueue([]); }, [me]);
  // Refresh the queue right when the operator lands on a Jobs page (don't wait for the 60s tick).
  useEffect(() => { poll(); }, [loc.pathname, poll]);   // a fresh check whenever the operator moves around

  const current = queue[0];

  // ── Workflow-deliverables composer state ──────────────────────────────────────────────────────
  // Declared BEFORE the early return — hooks must run in the same order on every render, and the
  // popup returns null whenever the queue is empty.
  const [subj, setSubj] = useState('');
  const [extra, setExtra] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const curId = current?.id || '';
  const curWf = current?.wf as undefined | {
    linked: boolean; link: string; files: Array<{ id: string; name: string; size: number; step: string }>;
    totalSize: number; overCap: boolean; cap: number; defaultHtml: string;
  };
  // Re-seed whenever a different confirmation reaches the front of the queue.
  useEffect(() => {
    setSubj(''); setExtra(curWf?.defaultHtml || '');
    setPicked(curWf && !curWf.overCap ? curWf.files.map(f => f.id) : []);
  }, [curId]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (!me || !current || publicPage || typing) return null;

  const client = `${current.first_name || ''} ${current.last_name || ''}`.trim() || '—';
  const finish = current.finish_unknown ? t('jc_unknown') : fmtDate(current.finish_date);
  const finishChanged = Number(current.finish_changed) === 1;
  const oldFinishStr = current.old_finish_unknown ? t('jc_unknown')
    : (String(current.old_finish || '').trim() ? fmtDate(current.old_finish) : '—');
  // When the finish date is what changed, make it stand out (amber highlight + "was …") so the
  // operator re-verifies the new date specifically.
  const finishNode: React.ReactNode = finishChanged ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ background: '#FEF3C7', color: '#92400E', border: '1px solid #FCD34D', borderRadius: 6, padding: '2px 9px', fontWeight: 800 }}>{finish}</span>
      <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.3, color: '#B45309', background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 999, padding: '1px 7px' }}>{t('jc_changed')}</span>
      <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t('jc_was', { v: oldFinishStr })}</span>
    </span>
  ) : finish;
  const { to, cc } = recipientPreview(current);
  const drop = (id: string) => setQueue(q => q.filter(x => x.id !== id));

  // ── Workflow deliverables (owner 2026-08-24) ──────────────────────────────────────────────────
  // Present only when the server decided this row qualifies: a workflow-linked job moving to
  // 4. Complete. The operator edits the subject, the paragraph and which files go — or presses
  // "Status Only" to send the plain status email with nothing attached.
  const wf = curWf;
  const pickedSize = (wf?.files || []).filter(f => picked.includes(f.id)).reduce((n, f) => n + f.size, 0);
  const tooBig = !!wf && (wf.overCap || pickedSize > wf.cap);
  const mb = (n: number) => `${(n / 1024 / 1024).toFixed(1)} MB`;

  const send = async (withDocs: boolean) => {
    if (busy) return;
    // Cancelling the documents must not cancel the announcement — say so before it goes.
    if (wf && !withDocs && !window.confirm(t('jc_status_only_confirm'))) return;
    setBusy(true);
    try {
      const r: any = await api.jobConfirmSend(current.id, withDocs
        ? { withDocs: true, subject: subj.trim() || undefined, extraHtml: extra.trim() || undefined, fileIds: tooBig ? [] : picked }
        : { withDocs: false, subject: subj.trim() || undefined });
      showToast(r?.noChange ? t('jc_nochange') : r?.attached ? t('jc_sent_docs', { n: r.attached }) : t('jc_sent'));
      drop(current.id);
    } catch (e: any) {
      setBlockingError(`Job #${Number(current.job_no).toLocaleString()}\n\n${e?.message || e}\n\nThe confirmation is still pending — it will pop up again so you can retry.`);
    } finally { setBusy(false); }
  };

  const onConfirm = async () => {
    if (busy) return;
    if (wf?.linked) return send(true);
    setBusy(true);
    try {
      const r: any = await api.jobConfirmSend(current.id, calPlan ? { calendarPlan: calPlan } : undefined);
      // A completion email is parked for approval rather than sent, so say which happened.
      showToast(r?.noChange ? t('jc_nochange') : r?.queued ? t('jc_queued') : t('jc_sent'));
      drop(current.id);
    } catch (e: any) {
      setBlockingError(`Job #${Number(current.job_no).toLocaleString()}\n\n${e?.message || e}\n\nThe confirmation is still pending — it will pop up again so you can retry.`);
    } finally { setBusy(false); }
  };
  const onRecheck = async () => {
    if (busy) return;
    setBusy(true);
    try { await api.jobConfirmDefer(current.id); } catch { /* still opens the editor */ }
    drop(current.id);
    setBusy(false);
    navigate(`/m/active_jobs?open=${current.job_id}&edit=1`);
  };
  // Revert → restore the previous status and cancel the pending email; refresh the Jobs list so
  // the reverted status shows immediately behind the popup.
  const onRevert = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r: any = await api.jobConfirmRevert(current.id);
      showToast(t('jc_reverted', { status: plLabel(r?.oldLevel || current.old_level || '') }));
      // detail.jobId lets an OPEN JobDetailPanel re-fetch too (not just the list) — otherwise a
      // stale panel keeps showing (and could re-save) the values the revert just discarded.
      window.dispatchEvent(new CustomEvent('slf:jobs-changed', { detail: { jobId: current.job_id } }));
      drop(current.id);
    } catch (e: any) { showToast(e?.message || 'Revert failed'); }
    finally { setBusy(false); }
  };

  const kv = (label: string, value: React.ReactNode, strong = false) => (
    <tr>
      <td style={{ padding: '4px 14px 4px 0', color: 'var(--text-tertiary)', whiteSpace: 'nowrap', verticalAlign: 'top', fontSize: 12 }}>{label}</td>
      <td style={{ padding: '4px 0', fontSize: 13, fontWeight: strong ? 700 : 400, color: strong ? 'var(--accent-text)' : 'var(--text-primary)' }}>{value}</td>
    </tr>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--bg-surface)', borderRadius: 14, width: 500, maxWidth: '94vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ padding: '18px 22px 10px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <i className="ti ti-mail-forward" style={{ fontSize: 22, color: 'var(--accent)' }} aria-hidden />
            <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)' }}>{t('jc_title')}</div>
            {queue.length > 1 && <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)', background: 'var(--c-gray-100)', borderRadius: 999, padding: '2px 9px' }}>1 / {queue.length}</span>}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.5 }}>{t('jc_subtitle')}</div>
        </div>

        <div style={{ padding: '14px 22px 4px' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}><tbody>
            {kv(t('jc_job'), `#${Number(current.job_no).toLocaleString()} · ${current.specific_job || '—'}`)}
            {kv(t('jc_client'), client)}
            {/* Both levels as the Jobs screens print them — 'Immigration', an awaiting level's bare word with no symbol
                in front (report c0b2267f; report 22f86d4d had put Calem's symbol there), '3. Review (Client)'
                (LevelLabel). The revert toast above is text only and prints the same word (plLabel). */}
            {kv(t('jc_prev'), current.old_level ? <LevelLabel v={current.old_level} /> : '—')}
            {kv(t('jc_new'), (current.process_level || current.new_level) ? <LevelLabel v={current.process_level || current.new_level} /> : '—', true)}
            {kv(t('jc_finish'), finishNode, !finishChanged)}
            {current.comments ? kv(t('jc_comments'), <span style={{ whiteSpace: 'pre-wrap' }}>{String(current.comments).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}</span>) : null}
          </tbody></table>
        </div>

        {wf?.linked && (
          <div style={{ margin: '10px 22px', padding: '12px 14px', border: '1px solid #A7F3D0', borderRadius: 10, background: '#F0FDF4' }}>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: '#0E7C66', marginBottom: 8 }}>
              <i className="ti ti-paperclip" style={{ marginRight: 5 }} aria-hidden />{t('jc_wf_title')}
            </div>
            {tooBig ? (
              // Owner's rule: over the cap the email carries the LINK, never a partial file list.
              <div style={{ fontSize: 12, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 10px', marginBottom: 8 }}>
                <i className="ti ti-alert-triangle" style={{ marginRight: 5 }} aria-hidden />
                {t('jc_wf_toobig', { size: mb(wf.overCap ? wf.totalSize : pickedSize), cap: mb(wf.cap) })}
              </div>
            ) : wf.files.length ? (
              <div style={{ marginBottom: 8 }}>
                {wf.files.map(f => (
                  <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, padding: '2px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={picked.includes(f.id)} style={{ cursor: 'pointer' }}
                      onChange={e => setPicked(p => (e.target.checked ? [...p, f.id] : p.filter(x => x !== f.id)))} />
                    <span style={{ fontWeight: 600 }}>{f.name}</span>
                    <span style={{ color: 'var(--text-tertiary)' }}>{Math.max(1, Math.round(f.size / 1024))} KB · {f.step}</span>
                  </label>
                ))}
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('jc_wf_total', { n: picked.length, size: mb(pickedSize) })}</div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 8 }}>{t('jc_wf_none')}</div>
            )}
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t('jc_wf_subject')}</label>
            <input value={subj} onChange={e => setSubj(e.target.value)} placeholder={t('jc_wf_subject_ph')}
              style={{ width: '100%', margin: '3px 0 8px', padding: '7px 9px', fontSize: 12.5, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)' }} />
            <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)' }}>{t('jc_wf_message')}</label>
            <textarea value={extra} onChange={e => setExtra(e.target.value)} rows={5}
              style={{ width: '100%', marginTop: 3, padding: '7px 9px', fontSize: 11.5, fontFamily: 'ui-monospace, monospace', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', resize: 'vertical' }} />
            <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>{t('jc_wf_hint')}</div>
          </div>
        )}

        {String(current?.new_level || '').trim() === '4. Complete' && (
          <CompletionDocs key={current.job_id} jobId={current.job_id} onState={setDocState} onPlan={setCalPlan} />
        )}

        <div style={{ margin: '10px 22px', padding: '10px 12px', background: 'var(--c-gray-50)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 11.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
          <div><b style={{ color: 'var(--text-tertiary)' }}>{t('jc_to')}:</b> {to}</div>
          <div><b style={{ color: 'var(--text-tertiary)' }}>{t('jc_cc')}:</b> {cc}</div>
        </div>

        <div style={{ display: 'flex', gap: 8, padding: '8px 22px 20px', flexWrap: 'wrap' }}>
          {!(docState?.gated && docState.missing.length) && <button onClick={onConfirm} disabled={busy} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 9, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1 }}>
            <i className="ti ti-send" style={{ fontSize: 14 }} aria-hidden />{busy ? t('jc_sending') : t('jc_confirm_send')}
          </button>}
          {wf?.linked && (
            <button onClick={() => send(false)} disabled={busy} title={t('jc_status_only_hint')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)', fontWeight: 600, fontSize: 13, cursor: busy ? 'not-allowed' : 'pointer' }}>
              <i className="ti ti-mail-off" style={{ fontSize: 14 }} aria-hidden />{t('jc_status_only')}
            </button>
          )}
          <button onClick={onRecheck} disabled={busy} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-primary)', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            <i className="ti ti-pencil" style={{ fontSize: 14 }} aria-hidden />{t('jc_recheck')}
          </button>
          <button onClick={onRevert} disabled={busy} title={t('jc_revert_hint')} style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 9, border: '1px solid rgba(226,75,74,0.35)', background: 'transparent', color: '#E24B4A', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            <i className="ti ti-arrow-back-up" style={{ fontSize: 14 }} aria-hidden />{t('jc_revert')}
          </button>
        </div>
      </div>
    </div>
  );
}
